import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { localStorageKeys } from 'lib/consts/browserStorage'
import { navatticTourIds } from './useNavatticTour.consts'

export type TourId = (typeof navatticTourIds)[keyof typeof navatticTourIds]

type TourStatus = 'active' | 'passed' | 'skipped'

type TourConfig = Partial<
  Record<
    TourId,
    {
      status: TourStatus
    }
  >
>

interface NavatticTourStore {
  tours: TourConfig
  loginDismissals: Partial<Record<TourId, boolean>>
  setTourStatus(tourName: TourId, status: TourStatus): void
  dismissUntilLogin(tourName: TourId): void
  clearLoginDismissals(): void
}

export const useNavatticTourStore = create<NavatticTourStore>()(
  persist(
    (set, get) => ({
      tours: {
        [navatticTourIds.PRODUCT_TOUR]: {
          status: 'active',
        },
      },
      loginDismissals: {},
      setTourStatus: (tourName, status) => {
        set({
          tours: {
            ...get().tours,
            [tourName]: {
              status,
            },
          },
        })
      },
      dismissUntilLogin: (tourName) => {
        set({
          loginDismissals: {
            ...get().loginDismissals,
            [tourName]: true,
          },
        })
      },
      clearLoginDismissals: () => {
        set({ loginDismissals: {} })
      },
    }),
    {
      version: 1,
      name: localStorageKeys.navatticTour,
      migrate: (state) => {
        interface PreReleaseStateVersion {
          tours: {
            productTour: {
              status: TourStatus
            }
          }
        }

        const isPreReleaseStateVersion = (state: unknown): state is PreReleaseStateVersion =>
          Boolean(
            typeof state === 'object' &&
              state !== null &&
              'tours' in state &&
              typeof state.tours === 'object' &&
              state.tours !== null &&
              'productTour' in state.tours,
          )

        if (isPreReleaseStateVersion(state)) {
          return {
            tours: {
              [navatticTourIds.PRODUCT_TOUR]: {
                status: state.tours.productTour.status,
              },
            },
          }
        }

        return state
      },
    },
  ),
)

export const useNavatticTour = () => {
  const { tours, loginDismissals, setTourStatus, dismissUntilLogin, clearLoginDismissals } = useNavatticTourStore()

  const completeTour = (tourId: TourId) => setTourStatus(tourId, 'passed')
  const skipTour = (tourId: TourId) => setTourStatus(tourId, 'skipped')
  const isTourActive = (tourId: TourId) => tours[tourId]?.status === 'active'
  const isDismissedUntilLogin = (tourId: TourId) => Boolean(loginDismissals[tourId])
  const isNavatticTour = (tourId: string): tourId is TourId => Object.values(navatticTourIds).includes(tourId as TourId)

  return {
    isTourActive,
    isDismissedUntilLogin,
    completeTour,
    skipTour,
    dismissUntilLogin,
    clearLoginDismissals,
    isNavatticTour,
  }
}
